import React, { forwardRef, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Z_INDEX } from '../../constants';
import {
  arrow,
  autoUpdate,
  FloatingArrow,
  offset,
  useDismiss,
  UseDismissProps,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { flip, shift, size } from '@floating-ui/dom';
import ReactPortal from '../ReactPortal';
import InlineDialogContext from './InlineDialogContext';
import isDefined from '../../isDefined';

export const INLINE_DIALOG_PORTAL_DIV_ID = 'INLINE_DIALOG_PORTAL_DIV_ID';

export type Placement =
  | 'top-start'
  | 'top'
  | 'top-end'
  | 'right-start'
  | 'right'
  | 'right-end'
  | 'bottom-end'
  | 'bottom'
  | 'bottom-start'
  | 'left-end'
  | 'left'
  | 'left-start';

interface Props {
  /** The elements that the InlineDialog will be positioned relative to. */
  children: React.ReactElement;
  /** The elements to be displayed within the InlineDialog. */
  content: React.ReactElement;
  /** Sets whether to show or hide the dialog. */
  isOpen?: boolean;
  /** Function called when the dialog is open and a click occurs anywhere outside
   the dialog. */
  onClose?: () => void;
  /** Where the dialog should appear, relative to the contents of the children. */
  placement?: Placement;
  isClickOutsideToCloseDisabled?: boolean;
  isAnchorDisplayFlex?: boolean;
  hasArrow?: boolean;
  /** Makes the dialog exactly as wide as the elements it's positioned
   relative to (e.g. a dropdown's list matching its input). */
  isAnchorWidth?: boolean;
  /** Raises the dialog off what it floats over with a dropdown's shadow. */
  hasShadow?: boolean;
}

const BORDER_COLOR = 'var(--color-border-tertiary)';

const Container = styled.div<{
  fixedHeight?: boolean;
  $hasShadow?: boolean;
  ref?: any;
}>`
  background-color: var(--color-bg-primary);
  border-radius: 8px;
  box-sizing: content-box; /* do not set this to border-box or it will break the overflow handling */

  border: 1px solid ${BORDER_COLOR};

  max-width: unset;
  padding: 0px;
  max-height: unset;
  height: ${(props) => (props.fixedHeight ? '230px' : 'unset !important')};
  z-index: ${Z_INDEX.INLINE_DIALOG};
  ${(props) => (props.$hasShadow ? 'box-shadow: var(--shadow-dropdown);' : '')}

  &:focus {
    outline: none;
  }
`;

const Anchor = forwardRef<
  HTMLDivElement,
  {
    children: JSX.Element | JSX.Element[];
    isAnchorDisplayFlex?: boolean;
  }
>(
  (
    {
      children,
      isAnchorDisplayFlex,
    }: {
      children: JSX.Element | JSX.Element[];
      isAnchorDisplayFlex?: boolean;
    },
    ref,
  ) => (
    <div ref={ref} style={isAnchorDisplayFlex ? { width: '100%' } : undefined}>
      {children}
    </div>
  ),
);

const TEST_DISMISS_PROPS: UseDismissProps = {
  capture: true,
  referencePress: true,
  referencePressEvent: 'click',
  outsidePress: true,
  outsidePressEvent: 'click',
};

const InlineDialog = (props: Props) => {
  const { placement, isOpen, onClose, children, content, isAnchorDisplayFlex } =
    props;
  const onOpenChange = useCallback(
    (newStatus: boolean) => {
      if (newStatus === false && onClose) {
        onClose();
      }
    },
    [onClose],
  );

  const arrowRef = useRef(null);
  // Read from a ref: useFloating keeps the first render's middleware while
  // later ones stringify the same, so a closure over props would go stale
  const isAnchorWidthRef = useRef(props.isAnchorWidth);
  isAnchorWidthRef.current = props.isAnchorWidth;
  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange,
    placement: placement || 'bottom-start',
    strategy: 'absolute',
    middleware: [
      flip(),
      shift(),
      offset(8),
      // Publish the space available on the chosen side as a CSS var so tall
      // content can cap itself and scroll instead of overflowing the viewport.
      // Purely additive — dialogs that don't read the var are unaffected.
      size({
        padding: 12,
        apply({ availableHeight, elements, rects }) {
          elements.floating.style.setProperty(
            '--inline-dialog-available-height',
            `${Math.round(availableHeight)}px`,
          );
          if (isAnchorWidthRef.current) {
            // The container is content-box, so its 1px borders sit outside
            // this width
            elements.floating.style.width = `${rects.reference.width - 2}px`;
          }
        },
      }),
      props.hasArrow
        ? arrow({
            element: arrowRef,
          })
        : undefined,
    ].filter(isDefined),
  });
  const dismiss = useDismiss(
    context,
    process.env.NODE_ENV === 'test' ? TEST_DISMISS_PROPS : undefined,
  );
  const { getReferenceProps, getFloatingProps } = useInteractions(
    props.isClickOutsideToCloseDisabled ? [] : [dismiss],
  );

  const arrowColor = 'var(--color-bg-primary)';

  return (
    <>
      <Anchor
        ref={refs.setReference}
        {...getReferenceProps()}
        isAnchorDisplayFlex={isAnchorDisplayFlex}
      >
        {children}
      </Anchor>
      {isOpen && (
        <ReactPortal elementId={INLINE_DIALOG_PORTAL_DIV_ID}>
          <Container
            data-testid="inline-dialog-container"
            ref={refs.setFloating}
            $hasShadow={props.hasShadow}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {props.hasArrow && (
              <FloatingArrow
                ref={arrowRef}
                context={context}
                fill={arrowColor}
                stroke={BORDER_COLOR}
                strokeWidth={1}
                color={arrowColor}
              />
            )}

            <InlineDialogContext.Provider value={{ isInlineDialog: true }}>
              {content}
            </InlineDialogContext.Provider>
          </Container>
        </ReactPortal>
      )}
    </>
  );
};

export default InlineDialog;
